import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { Button, Spin } from "antd";
import { useSelector } from "react-redux";
import { Link, Navigate } from "react-router-dom";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import freightApi2 from "../../service/modules/freight.api";
import {
  CARRIER_APPLICATIONS_PAGE_SIZE,
  TAB_ACCEPTED,
  TAB_FINALIZED,
  TAB_LOST,
  TAB_WAITING,
  TABS_USING_ASSIGNED_FREIGHTS,
  filterApplicationsForTab,
  filterAssignedFreightsForTab,
  formatApplicationDate,
  formatWaitingSince,
  getApiErrorMessage,
  mapCarrierApplicationItem,
  mapFreightV2ToApplicationItem,
  parseCarrierApplicationsResponse,
  parseFreightV2ApplicationsResponse,
} from "./carrierApplications.mapper";
import { formatFreightValueDisplay } from "../lances/mockData";
import "./minhas-candidaturas.css";

const FREIGHT_LIST_ROUTE = "/freightlistCopy";

function ApplicationCard({ application, variant }) {
  const waitingSince = application.isWaiting
    ? formatWaitingSince(application.registeredAt)
    : null;

  const cardClass = {
    [TAB_WAITING]: "candidaturas-card--waiting",
    [TAB_ACCEPTED]: "candidaturas-card--accepted",
    [TAB_FINALIZED]: "candidaturas-card--finalized",
    [TAB_LOST]: "candidaturas-card--lost",
  }[variant];

  const stripClass = {
    [TAB_WAITING]: "candidaturas-card__strip--waiting",
    [TAB_ACCEPTED]: "candidaturas-card__strip--accepted",
    [TAB_FINALIZED]: "candidaturas-card__strip--finalized",
    [TAB_LOST]: "candidaturas-card__strip--lost",
  }[variant];

  return (
    <article className={`candidaturas-card ${cardClass}`}>
      <div className={`candidaturas-card__strip ${stripClass}`}>
        {application.applicationStatusLabel}
      </div>

      <div className="candidaturas-card__body">
        <div className="candidaturas-card__header">
          <div className="candidaturas-card__number">#{application.number}</div>
          <div className="candidaturas-card__shipper">{application.shipperName}</div>
          <div className="candidaturas-card__value">
            {formatFreightValueDisplay(
              application.fixedPriceAmount,
              application.pricingType,
            )}
          </div>
        </div>

        <div className="candidaturas-card__route">
          <LocalShippingIcon className="localShippingIcon" />
          <span>{application.origin}</span>
          <ArrowForwardIcon sx={{ fontSize: 16, color: "rgba(1,36,66,0.35)" }} />
          <span>{application.destination}</span>
        </div>

        <div className="candidaturas-card__meta">
          <div className="candidaturas-card__meta-row">
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#ff3100" }} />
            <span>Coleta: {formatApplicationDate(application.collectionDate)}</span>
          </div>
          {application.freightStatusDescribe ? (
            <div className="candidaturas-card__meta-row">
              <span>Status do frete: {application.freightStatusDescribe}</span>
            </div>
          ) : null}
          {variant === TAB_LOST && application.acceptedCarrierName ? (
            <div className="candidaturas-card__meta-row">
              <span>Transportadora aceita: {application.acceptedCarrierName}</span>
            </div>
          ) : null}
          {variant === TAB_LOST && application.lostReasonLabel ? (
            <div className="candidaturas-card__meta-row candidaturas-card__meta-row--lost">
              <span>{application.lostReasonLabel}</span>
            </div>
          ) : null}
          {variant === TAB_ACCEPTED && application.registeredAt ? (
            <div className="candidaturas-card__meta-row">
              <span>
                Candidatura em {formatApplicationDate(application.registeredAt)}
              </span>
            </div>
          ) : null}
        </div>

        <div className="candidaturas-card__footer">
          {waitingSince ? (
            <span className="candidaturas-card__waiting">Na fila {waitingSince}</span>
          ) : (
            <span />
          )}

          {application.canGoToFreightList ? (
            <Link to={FREIGHT_LIST_ROUTE} style={{ textDecoration: "none" }}>
              <Button
                type="primary"
                size="small"
                className="candidaturas-btn-primary"
                icon={<OpenInNewOutlinedIcon style={{ fontSize: 16 }} />}
              >
                Ir para listagem de fretes
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
}

const EMPTY_TAB_MESSAGES = {
  [TAB_WAITING]: {
    title: "Nenhum frete em análise",
    description:
      "Fretes pendentes de contratação em que sua transportadora entrou na fila aparecerão aqui.",
    cta: { to: "/lances", label: "Ver fretes em Lances", primary: true },
  },
  [TAB_ACCEPTED]: {
    title: "Nenhum frete aceito ainda",
    description:
      "Fretes em que sua transportadora foi definida como responsável pelo transporte aparecerão nesta aba.",
    cta: { to: FREIGHT_LIST_ROUTE, label: "Abrir listagem de fretes", primary: false },
  },
  [TAB_FINALIZED]: {
    title: "Nenhum frete finalizado",
    description:
      "Fretes finalizados em que sua transportadora foi a responsável pelo transporte aparecerão aqui.",
    cta: { to: FREIGHT_LIST_ROUTE, label: "Abrir listagem de fretes", primary: false },
  },
  [TAB_LOST]: {
    title: "Nenhum frete perdido",
    description:
      "Fretes em que você entrou na fila, mas não foi selecionado ou o frete foi cancelado, aparecerão nesta aba.",
    cta: { to: "/lances", label: "Ver fretes em Lances", primary: true },
  },
};

function EmptyTabState({ tab, hasCarrier }) {
  if (!hasCarrier) return null;

  const config = EMPTY_TAB_MESSAGES[tab] || EMPTY_TAB_MESSAGES[TAB_WAITING];

  return (
    <Box className="candidaturas-empty">
      <div className="candidaturas-empty__title">{config.title}</div>
      <p style={{ margin: "0 0 16px", maxWidth: 420, marginInline: "auto" }}>
        {config.description}
      </p>
      {config.cta ? (
        <Link to={config.cta.to}>
          <Button
            type={config.cta.primary ? "primary" : "default"}
            className={config.cta.primary ? "candidaturas-btn-primary" : "candidaturas-btn-outline"}
            id={config.cta.primary ? "ButtonHover" : undefined}
          >
            {config.cta.label}
          </Button>
        </Link>
      ) : null}
    </Box>
  );
}

const TAB_CONFIG = [
  { key: TAB_WAITING, label: "Em análise", icon: HourglassEmptyOutlinedIcon },
  { key: TAB_ACCEPTED, label: "Aceitas", icon: CheckCircleOutlineIcon },
  { key: TAB_FINALIZED, label: "Finalizado", icon: TaskAltOutlinedIcon },
  { key: TAB_LOST, label: "Perdido", icon: HighlightOffOutlinedIcon },
];

export default function MinhasCandidaturasPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [tabCount, setTabCount] = useState(0);
  const [activeTab, setActiveTab] = useState(TAB_WAITING);

  const canView =
    user?.permissoes?.freightCarrierApplications?.isView || user?.viewALL;

  const idCarrier = user?.uidShipper;

  const loadApplications = useCallback(
    async ({ cursor, append = false, tab = activeTab } = {}) => {
      if (!idCarrier) {
        setApplications([]);
        setNextCursor(null);
        setHasMore(false);
        setTabCount(0);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        if (TABS_USING_ASSIGNED_FREIGHTS.includes(tab)) {
          const params = {
            uidShipper: idCarrier,
            isTransportadora: true,
            limit: CARRIER_APPLICATIONS_PAGE_SIZE,
          };
          if (tab === TAB_FINALIZED) {
            params.status = "Finalizado";
          }
          if (cursor) params.cursor = cursor;

          const response = await freightApi2.getAllFreightsV2(params);
          const parsed = parseFreightV2ApplicationsResponse(response, cursor);
          const filteredRaw = filterAssignedFreightsForTab(parsed.items, tab);
          const mapped = filteredRaw.map((item) =>
            mapFreightV2ToApplicationItem(item, tab),
          );

          setApplications((prev) => (append ? [...prev, ...mapped] : mapped));
          setNextCursor(parsed.nextCursor);
          setHasMore(parsed.hasMore);
          setTabCount(parsed.count ?? mapped.length);
          return;
        }

        const params = {
          idCarrier,
          tab,
          limit: CARRIER_APPLICATIONS_PAGE_SIZE,
        };
        if (cursor) params.cursor = cursor;

        const response = await freightApi2.getCarrierApplications(params);
        const parsed = parseCarrierApplicationsResponse(response, cursor);
        const mapped = parsed.items
          .map(mapCarrierApplicationItem)
          .filter((item) => filterApplicationsForTab([item], tab).length > 0);

        setApplications((prev) => (append ? [...prev, ...mapped] : mapped));
        setNextCursor(parsed.nextCursor);
        setHasMore(parsed.hasMore);
        setTabCount(mapped.length);
      } catch (err) {
        console.error("[MinhasCandidaturas] Erro ao carregar candidaturas:", err);
        setError(
          getApiErrorMessage(
            err,
            "Não foi possível carregar suas candidaturas.",
          ),
        );
        if (!append) {
          setApplications([]);
          setNextCursor(null);
          setHasMore(false);
          setTabCount(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [idCarrier, activeTab],
  );

  useEffect(() => {
    if (isLoggedIn && canView) {
      loadApplications({ tab: activeTab });
    }
  }, [isLoggedIn, canView, activeTab, loadApplications]);

  const handleRefresh = () => {
    loadApplications({ tab: activeTab });
  };

  const handleLoadMore = () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    loadApplications({ cursor: nextCursor, append: true, tab: activeTab });
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setApplications([]);
    setNextCursor(null);
    setHasMore(false);
  };

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (!canView) {
    return <Navigate to="/" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <div className="freight-content candidaturas-page">
          <div className="candidaturas-page__toolbar">
            <div>
              <h3>Meus lances</h3>
              <p className="candidaturas-page__subtitle">
                Em análise: fretes pendentes de contratação em que você está na fila.
                Aceitas e Finalizado: fretes atribuídos à sua transportadora.
                Perdido: fretes em que você entrou na fila, mas não venceu.
              </p>
            </div>
            <Button
              type="primary"
              id="ButtonHover"
              icon={<RefreshOutlinedIcon style={{ fontSize: 16 }} />}
              onClick={handleRefresh}
              loading={loading}
              disabled={!idCarrier}
            >
              Atualizar
            </Button>
          </div>

          {!idCarrier ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Usuário sem transportadora vinculada. Não é possível listar candidaturas.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <div className="candidaturas-tabs" role="tablist">
            {TAB_CONFIG.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                className={`candidaturas-tab ${
                  activeTab === key ? "candidaturas-tab--active" : ""
                }`}
                onClick={() => handleTabChange(key)}
              >
                <Icon sx={{ fontSize: 18 }} />
                {label}
                <span className="candidaturas-tab__count">
                  {activeTab === key ? tabCount : "—"}
                </span>
              </button>
            ))}
          </div>

          {loading && applications.length === 0 ? (
            <div className="candidaturas-loading">
              <Spin size="large" />
            </div>
          ) : null}

          {!loading && applications.length === 0 ? (
            <EmptyTabState tab={activeTab} hasCarrier={Boolean(idCarrier)} />
          ) : null}

          {applications.length > 0 ? (
            <div className="candidaturas-list">
              {applications.map((application) => (
                <ApplicationCard
                  key={`${application.freightId}_${application.applicationStatus}_${application.updatedAt || ""}`}
                  application={application}
                  variant={activeTab}
                />
              ))}
            </div>
          ) : null}

          {hasMore && applications.length > 0 ? (
            <div className="candidaturas-load-more">
              <Button
                className="candidaturas-btn-outline"
                onClick={handleLoadMore}
                loading={loadingMore}
              >
                Carregar mais
              </Button>
            </div>
          ) : null}
        </div>
      }
    />
  );
}
